// src/components/NavRail.jsx
//
// Sidebar nav rail. Ported from the legacy monolith's NavRail
// (Ripple Workplace App.html line 9158). 72px wide, full-height,
// dark-blue background, sticky to the top.
//
// Design rules (do not silently relax):
//   - Item dimensions, padding, gap, font sizes, and colors come from
//     the legacy implementation byte-for-byte.
//   - Active state uses the FILLED icon variant + T.navActivePill bg +
//     T.hdrFrom icon/label color; inactive uses outline icon
//     + T.navItemMuted text.
//   - The admin gear sits at the bottom (marginTop: auto) and is
//     rendered ONLY when currentRole === "admin". Any other role —
//     including "unknown" or null while the role probe is in flight
//     — gets nothing in that slot.
//   - aria-current="page" is set on the active item so existing
//     Playwright specs that key off it (reportsLanding.spec.js) stay
//     green without rewriting.
//
// ADMIN-NAV-FEATURE-TOGGLES: Nav items are now filtered by DB-driven flags
// via useFeatureFlags(). ALL_NAV_ITEMS is the full ordered list;
// filterNavItems(flags) computes the visible subset — both are exported for
// unit tests (tests mock useFeatureFlags and call filterNavItems directly).
//
// ADMIN-NAV-TOGGLES-V2: filterNavItems now accepts nav_order as a second
// argument so the left-nav rows render in the admin-defined sort order.

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./NavRail.css";
import { T } from "../app/tokens.js";
import { NavRailIcon } from "./NavRailIcon.jsx";
import { useFeatureFlagContext } from "../contexts/FeatureFlagContext.jsx";
import { useSensitivityPolicy } from "../contexts/SensitivityPolicyContext.jsx";
import { governedNavEnabled } from "../app/governedNav.js";
import { useDataReconciliationSummary } from "../hooks/useDataReconciliationSummary.js";

// ROUTING-PHASE-1: Canonical path for each nav item.
// Used by NavButton to navigate declaratively when clicked.
const NAV_VIEW_TO_PATH = {
  places:           "/places",
  "start-here":     "/start-here",
  home:             "/home",
  // NAV-RESTRUCTURE-RADAR: nav "Radar" item navigates to /infocus-radar (the
  // InFocusRadarPage). The old /radar path (Leadership Radar) stays intact but
  // is no longer in the main nav.
  radar:            "/infocus-radar",
  playbooks:        "/playbooks",
  portfolio:        "/offices",
  leases:           "/leases",
  reviews:          "/reviews",
  plan:             "/markets",
  projects:         "/projects",
  okrs:             "/okrs",
  "reports-landing":"/reports",
  admin:            "/admin",
  roadmap:          "/roadmap",
  workflows:        "/workflows",
};

/** Map a URL pathname to the NavRail active-view key string.
 *  Returns null when there is no match so the caller can fall back to a prop. */
function pathnameToNavView(pathname) {
  if (!pathname) return null;
  if (/^\/(?:places|offices|markets|market-plan)(?:\/|$)/.test(pathname)) return "places";
  if (pathname.startsWith("/home"))         return "home";
  // NAV-RESTRUCTURE-RADAR: /infocus-radar now resolves to "radar" (the Radar
  // nav item) instead of "home". /radar (Leadership Radar) also maps to "radar"
  // but is not in the main nav — both highlight the same nav button when active.
  if (pathname.startsWith("/infocus-radar")) return "radar";
  if (pathname.startsWith("/radar"))         return "radar";
  if (pathname.startsWith("/playbooks"))     return "playbooks";
  if (pathname.startsWith("/offices"))      return "portfolio";
  if (pathname.startsWith("/leases"))       return "leases";
  if (pathname.startsWith("/decisions"))    return "radar";
  if (pathname.startsWith("/reviews"))      return "reviews";
  if (pathname.startsWith("/markets"))      return "plan";
  if (pathname.startsWith("/projects"))     return "projects";
  if (pathname.startsWith("/okrs"))        return "okrs";
  if (pathname.startsWith("/reports"))      return "reports-landing";
  if (pathname.startsWith("/admin") || pathname.startsWith("/imports")) return "admin";
  if (pathname.startsWith("/roadmap"))      return "roadmap";
  if (pathname.startsWith("/workflows"))    return "workflows";
  if (pathname.startsWith("/start-here"))   return "start-here";
  return null;
}

// Same shape as legacy NAV_ITEMS (line 425). Each entry maps a sidebar
// button to the set of view-enum strings it should highlight as active.
// `targetView` is the view set when the button is clicked.
//
// NAV-RESTRUCTURE-RADAR: The registry keeps every destination, including
// dormant entries. The persisted nav_order remains authoritative; this order
// is the compatibility fallback used before a governed order is available.
// Core destinations are intentionally flat and ordered:
//   Home · Markets · Offices · Leases · Projects · Playbooks · Reports
export const ALL_NAV_ITEMS = [
  // The visible label is "Home" but the internal key stays "home".
  { key: "home",        label: "Home",        views: ["home"],                         targetView: "home",            group: "action"    },
  { key: "places", label: "Places", views: ["places", "portfolio", "plan", "location-detail"], targetView: "places", group: "reference" },
  { key: "leases",      label: "Leases",      views: ["leases"],                       targetView: "leases",          group: "reference" },
  // PROJ-PR-1: Projects nav item; gated by FEATURE_PROJECTS_NAV.
  { key: "projects",    label: "Projects",    views: ["projects", "project-detail"],   targetView: "projects",        group: "reference" },
  { key: "okrs",        label: "OKRs",        views: ["okrs"],                         targetView: "okrs",             group: "reference" },
  // P-38: Playbooks launcher/library. Always enabled (not admin-gated).
  { key: "playbooks",   label: "Playbooks",   views: ["playbooks"],                    targetView: "playbooks",       group: "action"    },
  { key: "reports",     label: "Reports",     views: ["reports-landing"],              targetView: "reports-landing", group: "record"    },
  // NAV-RESTRUCTURE-RADAR: Radar remains a governed optional destination.
  { key: "radar",       label: "Radar",       views: ["radar", "infocus-radar"],       targetView: "infocus-radar",   group: "action"    },
  // Signals has no standalone route; its historical Admin row is not a
  // registered destination. Keep it out of both enabled and preview rails.
  // P-19: Reviews register — manual check-off register; gated by FEATURE_REVIEWS_NAV (default OFF).
  { key: "reviews",     label: "Reviews",     views: ["reviews"],                      targetView: "reviews",         group: "reference" },
  // ADMIN-NAV-ADD-ROADMAP: Roadmap nav item; gated by FEATURE_ROADMAP_NAV. Default OFF.
  { key: "roadmap",     label: "Roadmap",     views: ["roadmap"],                      targetView: "roadmap",         group: "reference" },
  // TASK-4557: Workflows nav item; gated by FEATURE_WORKFLOWS_NAV. Default OFF.
  { key: "workflows",   label: "Actions",     views: ["workflows"],                    targetView: "workflows",       group: "reference" },
];

// Kept for compatibility with consumers that inspect the registry metadata.
// Groups no longer control rail presentation or admin ordering.
const NAV_GROUP_ORDER = ["action", "reference", "record", "system"];

// Derived for compatibility only. The live rail is intentionally ungrouped.
export const NAV_GROUPS = NAV_GROUP_ORDER.map((g) =>
  ALL_NAV_ITEMS.filter((item) => item.group === g).map((item) => item.key),
).filter((keys) => keys.length > 0);

/**
 * Resolve the DB-owned ordered contract into registry metadata. This keeps
 * membership, labels, and position in one ordered list for both the
 * legacy rail and the v2 rail.
 */
export function getOrderedNavItems(navOrder, navLabels = {}, navConfig = []) {
  const registryByKey = new Map(ALL_NAV_ITEMS.map((item) => [item.key, item]));
  const configByKey = new Map(
    Array.isArray(navConfig)
      ? navConfig
          .filter((entry) => entry && typeof entry === "object" && entry.key)
          .map((entry) => [entry.key, entry])
      : [],
  );
  const source = Array.isArray(navOrder)
    ? navOrder
    : Array.isArray(navConfig) && navConfig.length > 0
      ? navConfig
      : ALL_NAV_ITEMS.map((item) => item.key);

  return source
    .map((entry) => {
      const key = typeof entry === "string" ? entry : entry?.key;
      const item = registryByKey.get(key);
      if (!item) return null;
      const configEntry = configByKey.get(key);
      const entryLabel = typeof entry === "object" && entry ? entry.label : null;
      const governedEnabled = typeof entry?.enabled === "boolean"
        ? entry.enabled
        : configEntry?.enabled;
      const label = typeof navLabels[key] === "string"
        ? navLabels[key]
        : typeof entryLabel === "string"
          ? entryLabel
          : null;
      return {
        ...item,
        ...(label !== null ? { label } : {}),
        ...(typeof governedEnabled === "boolean" ? { governedEnabled } : {}),
      };
    })
    .filter(Boolean);
}

/**
 * Return the exact ordered rail contents, including disabled preview entries
 * when the admin preview contract allows them. Disabled entries are returned
 * in-place rather than appended after active navigation.
 */
export function getRailNavItems(
  flags,
  navOrder,
  navLabels = {},
  { previewMode = false, isAdmin = false, navConfig = [], canSee = () => true } = {},
) {
  const ordered = getOrderedNavItems(navOrder, navLabels, navConfig);
  const enabled = filterNavItems(flags, navOrder, navLabels, navConfig);
  const enabledKeys = new Set(enabled.map((item) => item.key));
  const preview = previewMode && isAdmin;
  const disabledKeys = new Set();
  const items = ordered.filter((item) => {
    if (item.key === "leases" && !canSee("leases.page")) return false;
    if (enabledKeys.has(item.key)) return true;
    if (!preview) return false;
    disabledKeys.add(item.key);
    return true;
  });
  return { items, disabledKeys };
}

/**
 * Returns the visible subset of ALL_NAV_ITEMS given the current flag values,
 * sorted by the admin-defined nav_order when provided.
 * Labels are overridden from navLabels (DB values) when present so the nav
 * and the admin panel always show the same user-facing strings.
 *
 * Exported for unit tests (tests mock useFeatureFlags then call this directly).
 *
 * @param {object}   flags      - result of useFeatureFlags()
 * @param {string[]} [navOrder] - ordered array of nav item keys from the server
 * @param {Record<string, string>} [navLabels]  - DB label overrides keyed by nav item key
 * @returns {typeof ALL_NAV_ITEMS}
 */
export function filterNavItems(flags, navOrder, navLabels = {}, navConfig = []) {
  const configured = getOrderedNavItems(navOrder, navLabels, navConfig);
  return configured.filter((item) => governedNavEnabled(flags, navConfig, item.key, navOrder));
}

// NAV_ITEMS is kept as a named export for backward compatibility with any
// consumers that imported it before ADMIN-NAV-FEATURE-TOGGLES.
// It now reflects the DEFAULT_FLAGS state (same as static-flag era).
// Prefer filterNavItems(useFeatureFlags()) inside components.
export const NAV_ITEMS = filterNavItems({
  FEATURE_PLACES_NAV:    true,
  FEATURE_PULSE_NAV:     false,
  FEATURE_RADAR_NAV:     true,
  FEATURE_PLAYBOOKS_NAV: true,
  FEATURE_PROJECTS_NAV:  true,
  FEATURE_PLAN_NAV:      true,
  FEATURE_ROADMAP_NAV:   false,
  FEATURE_WORKFLOWS_NAV: false,
});

const ADMIN_ITEM    = { key: "admin",    label: "Admin",    views: ["admin"],    targetView: "admin"    };

function NavButton({ item, isActive, onSelect, badgeCount = 0 }) {
  const navigate = useNavigate();
  const path = NAV_VIEW_TO_PATH[item.key] ?? NAV_VIEW_TO_PATH[item.targetView];
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        if (path) navigate(path);
        onSelect?.(item.targetView);
      }}
      title={item.label}
      aria-current={isActive ? "page" : undefined}
      data-nav-key={item.key}
      data-active={isActive ? "true" : "false"}
      style={{
        width: 64, minHeight: 52,
        borderRadius: 12,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: isActive ? T.navActivePill : "transparent",
        border: "none", cursor: "pointer", gap: 5,
        color: isActive ? T.hdrFrom : T.navItemMuted,
        padding: "6px 0", flexShrink: 0,
      }}
    >
      <span style={{ position: "relative", display: "inline-flex" }}>
        <NavRailIcon itemKey={item.key} filled={isActive} size={22} />
        {badgeCount > 0 && (
          <span
            data-testid="nav-reconciliation-count"
            aria-label={`${badgeCount} active reconciliation findings`}
            style={{
              position: "absolute",
              top: -5,
              right: -10,
              minWidth: 14,
              height: 14,
              padding: "0 3px",
              borderRadius: 8,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: T.red,
              color: T.white,
              fontSize: T.fsXxxs,
              fontWeight: T.fwBold,
              lineHeight: 1,
              boxSizing: "border-box",
            }}
          >
            {badgeCount}
          </span>
        )}
      </span>
      <span style={{
        fontSize: T.fsXxxs, fontWeight: T.fwSemibold, letterSpacing: 0.4,
        textTransform: "uppercase", lineHeight: 1,
      }}>
        {item.label}
      </span>
    </button>
  );
}

/**
 * A nav button rendered in a disabled/greyed state for admin preview overlay.
 * These items are OFF in the DB but admins see them when preview_mode is ON,
 * so they can visualise what the nav looks like before enabling items.
 */
function DisabledNavButton({ item }) {
  return (
    <button
      type="button"
      title={item.label}
      aria-disabled="true"
      data-nav-key={item.key}
      data-testid={`nav-disabled-item-${item.key}`}
      disabled
      style={{
        width: 64, minHeight: 52,
        borderRadius: 12,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: "transparent",
        border: "none", cursor: "default", gap: 5,
        color: T.navItemMuted,
        padding: "6px 0", flexShrink: 0,
        opacity: 0.4,
      }}
    >
      <NavRailIcon itemKey={item.key} filled={false} size={22} />
      <span style={{
        fontSize: T.fsXxxs, fontWeight: T.fwSemibold, letterSpacing: 0.4,
        textTransform: "uppercase", lineHeight: 1,
      }}>
        {item.label}
      </span>
    </button>
  );
}

/**
 * @param {object}   props
 * @param {string}   [props.currentView]  Fallback view enum (URL takes priority when inside a Router)
 * @param {string?}  props.currentRole    "admin" | "editor" | "viewer" | "unknown" | null
 * @param {(view: string) => void} props.onSelect  Called with the targetView when an item is clicked
 */
export function NavRail({ currentView: currentViewProp, currentRole, onSelect }) {
  const { flags, flagsReady, nav_order, nav_config, nav_labels } = useFeatureFlagContext();
  const sensitivity = useSensitivityPolicy();
  const { summary } = useDataReconciliationSummary(currentRole);

  // ROUTING-PHASE-1: derive active item from the URL when inside a Router;
  // fall back to the prop for test environments (MemoryRouter defaults to "/").
  const { pathname } = useLocation();
  const currentViewFromUrl = pathnameToNavView(pathname);
  const currentView = currentViewFromUrl ?? currentViewProp;
  // Do not filter or render nav items until the server has responded.
  // Rendering with DEFAULT_FLAGS and then switching to DB values causes the
  // appear-then-vanish flicker this component is designed to avoid.
  const { items: railItems, disabledKeys } = flagsReady
    ? getRailNavItems(
        flags,
        nav_order,
        nav_labels ?? {},
        {
          previewMode: flags.preview_mode,
          isAdmin: currentRole === "admin",
          navConfig: nav_config,
          canSee: sensitivity.canSee,
        },
      )
    : { items: [], disabledKeys: new Set() };

  return (
    <nav
      aria-label="Primary"
      data-testid="nav-rail"
      tabIndex={-1}
      style={{
        width: 72, flexShrink: 0,
        background: T.hdrFrom,
        display: "flex", flexDirection: "column", alignItems: "center",
        paddingTop: 52, paddingBottom: 20, gap: 2,
        position: "sticky", top: 0, height: "100vh",
        overflowY: "hidden", zIndex: 100, boxSizing: "border-box",
      }}
    >
      {railItems.map((item) => disabledKeys.has(item.key) ? (
        <DisabledNavButton key={item.key} item={item} />
      ) : (
        <NavButton
          key={item.key}
          item={item}
          isActive={item.views.includes(currentView)}
          onSelect={onSelect}
        />
      ))}

      {currentRole === "admin" && (
        <div style={{ marginTop: "auto", paddingTop: 8 }}>
          <NavButton
            item={ADMIN_ITEM}
            isActive={ADMIN_ITEM.views.includes(currentView)}
            onSelect={onSelect}
            badgeCount={summary?.activeCount ?? 0}
          />
        </div>
      )}
    </nav>
  );
}
